import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { markNotificationRead, useMarkNotificationRead, useNotifications } from "../api/notifications";
import { useTranslation } from "../hooks/useTranslation";
import { formatUserName } from "../types/user";
import type { NotificationResponse } from "../types/notification";
import { BellIcon } from "./icons/BellIcon";
import { CheckIcon } from "./icons/CheckIcon";
import { Spinner } from "./ui/Spinner";
import styles from "./NotificationBell.module.css";

export function NotificationBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: notifications = [] } = useNotifications();
  const markRead = useMarkNotificationRead();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const bellRef = useRef<HTMLDivElement | null>(null);

  const unread = notifications.filter((n) => !n.readAt);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      if (!bellRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open]);

  function handleNotificationClick(n: NotificationResponse) {
    if (!n.readAt) {
      markRead.mutate(n.id);
    }
    const path = n.entityType === "development_request" ? `/requests/${n.entityId}` : `/inventory/${n.entityId}`;
    navigate(path);
    setOpen(false);
  }

  async function markAllRead() {
    setIsMarkingAll(true);
    try {
      await Promise.all(unread.map((n) => markNotificationRead(n.id)));
      await qc.invalidateQueries({ queryKey: ["notifications"] });
    } finally {
      setIsMarkingAll(false);
    }
  }

  return (
    <div ref={bellRef} className={styles["root"]}>
      <button onClick={() => setOpen((o) => !o)} className={styles["trigger"]} aria-label={t("notifications.title")}>
        <BellIcon className={styles["iconBell"]} />
        {unread.length > 0 && <span className={styles["badge"]}>{unread.length > 9 ? "9+" : unread.length}</span>}
      </button>

      {open && (
        <div className={styles["panel"]}>
          <div className={styles["panelHeader"]}>
            <h3 className={styles["panelTitle"]}>{t("notifications.title")}</h3>
            {unread.length > 0 && (
              <button onClick={markAllRead} className={styles["panelAction"]} disabled={isMarkingAll}>
                {isMarkingAll ? <Spinner size="sm" /> : t("notifications.markAllRead")}
              </button>
            )}
          </div>
          <ul className={styles["list"]}>
            {unread.length === 0 ? (
              <li className={styles["empty"]}>{t("notifications.noNotifications")}</li>
            ) : (
              unread.map((n) => (
                <li key={n.id} className={styles["listItem"]}>
                  <div className={styles["listItemRow"]}>
                    <button
                      onClick={() => handleNotificationClick(n)}
                      className={classNames(
                        styles["notificationButton"],
                        n.readAt === null && styles["notificationUnread"],
                      )}
                    >
                      <p
                        className={classNames(
                          styles["notificationText"],
                          n.readAt === null && styles["notificationTextUnread"],
                        )}
                      >
                        <strong>{formatUserName(n.actor)}</strong>{' '}
                        {t(`notifications.actions.${n.action}`)}
                      </p>
                      <p className={styles["notificationEntity"]}>{n.entityName}</p>
                      <p className={styles["notificationTime"]}>{new Date(n.createdAt).toLocaleString()}</p>
                    </button>
                    <button
                      onClick={() => markRead.mutate(n.id)}
                      className={styles["markReadBtn"]}
                      aria-label={t("notifications.markAsRead")}
                    >
                      <CheckIcon className={styles["markReadIcon"]} />
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
